import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import Image from '../image';
import Iconify from '../iconify';
import RejectionFiles from './errors-rejection-files';

// ----------------------------------------------------------------------

export default function UploadAvatar({
  error,
  imagesPreview,
  file,
  disabled,
  helperText,
  handleRemoveImage,
  readOnly,
  square,
  sx,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    disabled,
    accept: {
      'image/*': [],
    },
    ...other,
  });

  const hasFile = !!file;

  const hasError = isDragReject || !!error;

  const imgUrl = typeof file === 'string' ? file : file?.preview || '';
  // console.log(imgUrl);
  const renderServerImage = (
    <Image
      alt="avatar"
      src={imagesPreview[0]?.url}
      sx={{
        width: 1,
        height: 1,
        borderRadius: square ? '5px' : '50%',
      }}
    />
  );

  const renderPreview = imgUrl && (
    <Image
      alt="avatar"
      src={imgUrl}
      sx={{
        width: 1,
        height: 1,
        borderRadius: square ? '5px' : '50%',
      }}
    />
  );

  const renderPlaceholder = (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      className="upload-placeholder"
      sx={{
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 9,
        borderRadius: square ? '5px' : '50%',
        position: 'absolute',
        color: 'text.disabled',
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
        transition: (theme) =>
          theme.transitions.create(['opacity'], {
            duration: theme.transitions.duration.shorter,
          }),
        '&:hover': {
          opacity: 0.72,
        },
        ...(hasError && {
          color: 'error.main',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
        }),
        ...(hasFile && {
          zIndex: 9,
          opacity: 0,
          color: 'common.white',
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.64),
        }),
      }}
    >
      <Iconify icon="solar:camera-add-bold" width={32} />

      <Typography variant="caption">{file ? 'Update photo' : 'Upload photo'}</Typography>
    </Stack>
  );

  // const renderServerPlaceholder = (
  //   <Stack
  //     onClick={handleRemoveImage}
  //     alignItems="center"
  //     justifyContent="center"
  //     spacing={1}
  //     className="upload-placeholder"
  //     sx={{
  //       top: 0,
  //       left: 0,
  //       width: 1,
  //       height: 1,
  //       zIndex: 9,
  //       borderRadius: '50%',
  //       position: 'absolute',
  //       color: 'text.disabled',
  //       bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
  //       transition: (theme) =>
  //         theme.transitions.create(['opacity'], {
  //           duration: theme.transitions.duration.shorter,
  //         }),
  //       '&:hover': {
  //         opacity: 0.72,
  //       },
  //       ...(hasError && {
  //         color: 'error.main',
  //         bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
  //       }),
  //       ...(hasFile && {
  //         zIndex: 9,
  //         opacity: 0,
  //         color: 'error.light',
  //         bgcolor: (theme) => alpha(theme.palette.grey[900], 0.64),
  //       }),
  //     }}
  //   >
  //     <Iconify icon="iconoir:trash-solid" width={28} />

  //     <Typography variant="caption">Remove pic</Typography>
  //   </Stack>
  // );

  const renderContent = (
    <Box
      sx={{
        width: 1,
        height: 1,
        overflow: 'hidden',
        borderRadius: square ? '5px' : '50%',
        position: 'relative',
      }}
    >
      {renderPreview}
      {renderPlaceholder}
    </Box>
  );

  const renderServerContent = (
    <Box
      sx={{
        width: 1,
        height: 1,
        overflow: 'hidden',
        borderRadius: square ? '5px' : '50%',
        position: 'relative',
      }}
    >
      {renderServerImage}
      {/* {renderServerPlaceholder} */}
    </Box>
  );

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          p: 1,
          m: 'auto',
          width: 144,
          height: 144,
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: square ? '5px' : '50%',
          border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            borderColor: 'error.main',
          }),
          ...(hasFile && {
            ...(hasError && {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            }),
            '&:hover .upload-placeholder': {
              opacity: 1,
            },
          }),
          ...sx,
        }}
      >
        <input {...getInputProps()} />

        {imagesPreview?.length >= 1 ? renderServerContent : renderContent}
      </Box>

      {helperText && helperText}

      <RejectionFiles fileRejections={fileRejections} />
    </>
  );
}

UploadAvatar.propTypes = {
  disabled: PropTypes.object,
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.object,
  sx: PropTypes.object,
  imagesPreview: PropTypes.any,
  handleRemoveImage: PropTypes.func,
  readOnly: PropTypes.bool,
  square: PropTypes.string,
};
